import stringify from 'safe-stable-stringify'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ResponseData = { response: Response; json: any; cached?: boolean }

export class RequestCache {
  private data: Map<string, Promise<ResponseData>> = new Map()

  constructor(private readonly expiry: number = 1000 * 10) {}

  async performRequest(
    url: string,
    options: RequestInit,
    bypass = false,
  ): Promise<ResponseData> {
    if (options.method !== 'GET') {
      const result = await this.requestData(url, options)
      this.clearMatching(url)
      return result
    }

    const key = `${url}|${stringify(options)}`
    const cached = bypass ? undefined : this.get(key)
    // Bypass both this cache and the browser's HTTP cache.
    const fetchOptions: RequestInit = bypass
      ? { ...options, cache: 'no-store' }
      : options
    const request =
      cached ?? this.remember(key, this.requestData(url, fetchOptions))
    const result = await request

    return {
      ...result,
      cached: Boolean(cached) && this.successful(result.response),
    }
  }

  get(key: string) {
    return this.data.get(key)
  }

  private remember(key: string, request: Promise<ResponseData>) {
    // Old requests and expiry timers must not remove a newer replacement.
    const evict = () => {
      if (this.get(key) === pending) this.data.delete(key)
    }
    const pending = (async () => {
      try {
        const result = await request
        if (!this.successful(result.response)) evict()
        return result
      } catch (error) {
        evict()
        throw error
      }
    })()

    // Share the in-flight request, including its single JSON read.
    this.data.set(key, pending)
    setTimeout(evict, this.expiry)
    return pending
  }

  private async requestData(
    url: string,
    options: RequestInit,
  ): Promise<ResponseData> {
    const response = await fetch(url, options)
    try {
      const json = response.status === 204 ? null : await response.json()
      return { response, json, cached: false }
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(`Unexpected server response: ${response.status}`)
      }
      throw error
    }
  }

  private successful(response: Response) {
    return response.ok || response.status === 304
  }

  private clearMatching(url: string) {
    const baseUrl = url.split('?')[0]
    for (const key of this.data.keys()) {
      if (key.startsWith(baseUrl)) this.data.delete(key)
    }
  }
}
